import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";

const SIGNS = [
  "Your books are regularly late, and you find out from your accountant. Not your bookkeeper.",
  "Questions go unanswered for days, and answers arrive without explanation.",
  "Reconciliations are skipped “until things slow down.” They never slow down.",
  "You’re doing meaningful work yourself because it’s faster than waiting.",
  "You simply can’t explain what you’re paying for.",
];

const STEPS = [
  {
    title: "Choose the moment",
    body: "The cleanest handover happens at a natural boundary. Month end, quarter end, or year end. Mid-crisis switches are possible, but a calm moment gives the new bookkeeper room to verify everything.",
  },
  {
    title: "Collect what you own",
    body: "You’re entitled to your records: the books themselves, reconciliations, statements, and reports. It’s normal to request a complete, exported copy. A good bookkeeper hands this over promptly.",
  },
  {
    title: "Agree on the handover plan",
    body: "Who tells whom, what date the switch takes effect, and how pending work is handled. Put it in writing, even a short email. It protects everyone, including the bookkeeper you’re leaving.",
  },
  {
    title: "Expect a verification period",
    body: "A careful new bookkeeper starts by verifying: reconciling key accounts, checking for gaps, and flagging anything odd before building on it. Treat this as a good sign, not slowness.",
  },
  {
    title: "Start the relationship with scope, in writing",
    body: "What will be done, how often, what it costs, and how you’ll communicate. We confirm all of it before any work begins. You should expect the same from anyone you hire.",
  },
];

export default function ChangingBookkeepers() {
  return (
    <>
      <Seo
        title="Changing Bookkeepers: A Practical Guide | Directive Optive Solutions"
        description="When to consider changing bookkeepers, what a clean handover involves, and the questions to ask a new bookkeeper. A plain-language guide, free to print."
        path="/resources/changing-bookkeepers"
      />
      <PageHeader
        eyebrow="Guide"
        title="Changing bookkeepers, without the drama."
        lead="Switching bookkeepers is more common than most owners think. And far more workable than staying stuck. Here’s what a clean handover actually involves."
        image="/img/photo-handshake.webp"
        imageAlt="A client handshake over a desk with a notebook"
      />

      <section className="py-24 lg:py-32" data-testid="changing-signs-section">
        <div className="container-shell grid gap-14 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-serif text-3xl text-olive sm:text-4xl">When it’s time to consider it</h2>
            <ul className="mt-7 space-y-4 text-base leading-relaxed text-moss">
              {SIGNS.map((s, i) => (
                <li key={s} className="flex gap-3" data-testid={`changing-sign-${i + 1}`}>
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-olive" /> {s}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="rounded-md border border-olive/25 bg-sage p-8">
            <h3 className="font-serif text-xl text-olive">A gentle word first</h3>
            <p className="mt-3 text-sm leading-relaxed text-moss">Some of these signs have honest explanations. A rough season, a systems change, a miscommunication. A direct conversation sometimes fixes more than a switch would. But if the pattern repeats after that conversation, it’s information too.</p>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-sage/85 py-24 lg:py-32" data-testid="changing-steps-section">
        <div className="container-shell relative z-10">
          <Reveal className="max-w-3xl">
            <p className="eyebrow">The handover</p>
            <h2 className="mt-5 font-serif text-3xl text-olive sm:text-4xl">Five steps to a clean switch</h2>
          </Reveal>
          <div className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.07} className="border-t-2 border-olive/25 pt-7">
                <span className="font-serif text-4xl italic text-olive/30">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-serif text-xl text-olive sm:text-2xl">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-moss">{s.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaSection heading="Thinking about a change? Start with a conversation." body="We take over books carefully. Verification first, explanations throughout. Book a consultation or send us a note about your current setup." />
    </>
  );
}
